import { useTranslation } from "react-i18next";
import { useMemo } from "react";

import { getFullPath } from "@/router/utils/getFullPath";
import { slugs } from "@/router/slugs";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/string";
import { Account, Invoice } from "@/api/opb/opbTypes";
import { isInvoiceLate } from "@/utils/invoice";
import { Button, ButtonVariant } from "@industriousoffice/react-components";

interface InvoiceTableFooterProps {
  account: Account;
  accountCurrencyCode: string;
  invoices: Invoice[];
  isLoading: boolean;
}
export function InvoiceTableFooter({
  account,
  accountCurrencyCode,
  invoices,
  isLoading,
}: InvoiceTableFooterProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation("dashboard");
  const navigate = useNavigate();

  const onNavigateToInvoices = () => {
    navigate(getFullPath(slugs.allInvoices, { accountId: account._id }));
  };

  const onNavigateToPayInvoices = () => {
    navigate(getFullPath(slugs.payInvoices, { accountId: account._id }));
  };
  if (isLoading) return null;

  const calculateTotalAmount = () =>
    invoices.reduce((total, invoice) => total + invoice.balance, 0);

  const countOverDueInvoices = useMemo(
    () => invoices.filter((i) => isInvoiceLate(i)).length,
    [invoices]
  );

  return (
    <>
      {invoices.length > 0 ? (
        <>
          {!!account.unappliedCreditMemoAmount && (
            <div className="flex flex-row items-center justify-between pt-4 pl-4 text-grey-30">
              <p>
                {t("Total Unapplied Credit Balance")}:
                <span className="pl-2 font-bold">
                  {formatCurrency({
                    // API returns this field in cents
                    value: account.unappliedCreditMemoAmount / 100,
                    currency: accountCurrencyCode,
                    lang: language,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
            </div>
          )}
          <div className="flex flex-row items-center justify-between py-4">
            <p className="pl-4 text-sm text-left text-grey-30 sm:text-base">
              {t("You have")}
              <span className="font-semibold">
                {t("invoices", {
                  count: countOverDueInvoices || invoices.length,
                })}
              </span>
              {t(countOverDueInvoices ? "overdue" : "due soon")}
            </p>
            <div className="items-end pr-4 text-xl font-bold sm:text-2xl text-end">
              {formatCurrency({
                value: calculateTotalAmount(),
                currency: accountCurrencyCode,
                lang: language,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              {` ${accountCurrencyCode}`}
            </div>
          </div>
        </>
      ) : null}
      <div className="px-4 pb-5 sm:pl-0 sm:flex-row sm:flex sm:items-center sm:justify-end">
        <div className="flex">
          <Button
            onClick={onNavigateToInvoices}
            className={`py-5 ${!invoices.length ? "max-sm:w-full" : ""}`}
            variant={ButtonVariant.Outline}
            data-testid="dashboard-view_all_invoices_account-btn"
          >
            {t("View All Invoices")}
          </Button>
          {invoices.length ? (
            <Button
              onClick={onNavigateToPayInvoices}
              className="py-5 ml-4"
              data-testid="dashboard-pay_invoices_account-btn"
            >
              {t("Pay Invoices")}
            </Button>
          ) : null}
        </div>
      </div>
    </>
  );
}
